// =============================================================================
// StudioThemePicker — 4 karty w jednym rzedzie (lg: 4, sm: 2, ponizej: 1):
// Brand / AstroPaper / Wlasny renderer / „Wgraj wlasny motyw · wkrotce" (disabled,
// ten sam rozmiar). Miniatura 16:10 (zrzuty home z fixture, public/theme-thumbs).
// Klik = zapis silnika + motywu; przejscie na Astro sprawdza /health build-workera.
// =============================================================================

import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Loader2, Upload } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { checkBuildWorker } from "@/services/staticSite/astroDeploy";
import type { StaticSiteRow, StaticSiteSettings } from "@/services/staticSite/types";

interface Props {
  site: StaticSiteRow;
  onSaveSettings: (patch: Partial<StaticSiteSettings>) => Promise<void>;
}

type Choice = "brand" | "astropaper" | "browser";

const CARDS: Array<{ id: Choice; label: string; desc: string; thumb: string }> = [
  { id: "brand", label: "Brand", desc: "Landing + blog: mocne hero, kategorie jako moduły, okładki, pasek CTA. Design z briefu poniżej.", thumb: "/theme-thumbs/brand.png" },
  { id: "astropaper", label: "AstroPaper", desc: "Klasyczny, minimalistyczny blog. Tryb jasny i ciemny.", thumb: "/theme-thumbs/astropaper.png" },
  { id: "browser", label: "Własny renderer", desc: "Motyw editorial budowany w przeglądarce, bez kontenera. Sprawdzony na pilocie.", thumb: "/theme-thumbs/editorial.jpg" },
];

function currentChoice(site: StaticSiteRow): Choice {
  const s: Partial<StaticSiteSettings> = site.settings || {};
  if (s.build_backend !== "astro") return "browser";
  return s.astro_theme === "astropaper" ? "astropaper" : "brand";
}

export function StudioThemePicker({ site, onSaveSettings }: Props) {
  const { toast } = useToast();
  const [busy, setBusy] = useState<Choice | null>(null);
  const active = currentChoice(site);

  const choose = async (id: Choice) => {
    if (id === active) return;
    setBusy(id);
    try {
      if (id !== "browser" && site.settings?.build_backend !== "astro") {
        const check = await checkBuildWorker();
        if (!check.ok) {
          toast({ title: "Build-worker niedostępny", description: check.message, variant: "destructive" });
          return;
        }
      }
      await onSaveSettings(id === "browser" ? { build_backend: "browser" } : { build_backend: "astro", astro_theme: id });
      toast({ title: `Motyw: ${CARDS.find((c) => c.id === id)?.label}` });
    } catch (e) {
      toast({ title: "Nie udało się zmienić motywu", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {CARDS.map((c) => {
        const on = c.id === active;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => void choose(c.id)}
            disabled={busy !== null}
            className={`flex flex-col overflow-hidden rounded-lg border text-left transition ${on ? "border-primary ring-2 ring-primary/40" : "border-border hover:border-primary/50"}`}
          >
            <div className="relative aspect-[16/10] w-full bg-muted">
              <img src={c.thumb} alt={c.label} className="h-full w-full object-cover object-top" loading="lazy" />
              {busy === c.id && <div className="absolute inset-0 flex items-center justify-center bg-background/60"><Loader2 className="h-5 w-5 animate-spin" /></div>}
            </div>
            <div className="p-2.5">
              <div className="flex items-center justify-between gap-2 text-sm font-medium">{c.label}{on && <Badge className="text-[10px]">aktywny</Badge>}</div>
              <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{c.desc}</p>
            </div>
          </button>
        );
      })}
      <button type="button" disabled className="flex flex-col overflow-hidden rounded-lg border border-dashed text-left opacity-70" title="W przygotowaniu (B3: katalog własnych motywów)">
        <div className="flex aspect-[16/10] w-full items-center justify-center bg-muted/40"><Upload className="h-6 w-6 opacity-50" /></div>
        <div className="p-2.5">
          <div className="flex items-center justify-between gap-2 text-sm font-medium">Wgraj własny motyw <Badge variant="outline" className="text-[10px]">wkrótce</Badge></div>
          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">Własny szablon Astro z katalogu motywów. W przygotowaniu.</p>
        </div>
      </button>
    </div>
  );
}
